import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {
  screenAlertReducer,
  initialScreenAlertState,
  animationConfigByAlertType,
} from "./utils";
import { ScreenAlertType } from "./types";
import { Alert, Box } from "@mui/material";
import { animated, useSpring } from "@react-spring/web";

const ScreenAlertContext = createContext<{
  setWarning: (message: string) => void;
  setError: (message: string) => void;
  setInfo: (message: string) => void;
  setSuccess: (message: string) => void;
}>({
  setWarning: () => {},
  setError: () => {},
  setInfo: () => {},
  setSuccess: () => {},
});

type dispatchAction = (message: string) => void;

export const ScreenAlertProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(
    screenAlertReducer,
    initialScreenAlertState
  );

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const alertSpring = useSpring({
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: {
      opacity: state.isOpen ? 1 : 0,
      transform: state.isOpen ? "translateY(0px)" : "translateY(-20px)",
    },
    config: animationConfigByAlertType[state.type ?? ScreenAlertType.Success],
  });

  const setError = useCallback<dispatchAction>((message) => {
    dispatch({ type: ScreenAlertType.Error, payload: { message } });
  }, []);
  const setWarning = useCallback<dispatchAction>((message) => {
    dispatch({ type: ScreenAlertType.Warning, payload: { message } });
  }, []);
  const setInfo = useCallback<dispatchAction>((message) => {
    dispatch({ type: ScreenAlertType.Info, payload: { message } });
  }, []);
  const setSuccess = useCallback<dispatchAction>((message) => {
    dispatch({ type: ScreenAlertType.Success, payload: { message } });
  }, []);

  const value = useMemo(
    () => ({
      setWarning,
      setError,
      setInfo,
      setSuccess,
    }),
    [setWarning, setError, setInfo, setSuccess]
  );

  useEffect(() => {
    if (state.isOpen) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        dispatch({ type: "clear" });
      }, 4000); // Auto-close after 5 seconds
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state.isOpen]);

  return (
    <ScreenAlertContext.Provider value={value}>
      {state.isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            marginRight: "-50%",
            zIndex: 1400, // above MUI Modal (1300) and Drawer (1200)
            width: "auto",
            maxWidth: "400px",
          }}
        >
          <animated.div style={alertSpring}>
            <Alert variant="filled" severity={state.type ?? "info"}>
              {state.message}
            </Alert>
          </animated.div>
        </Box>
      )}
      {children}
    </ScreenAlertContext.Provider>
  );
};

export const useScreenAlert = () => {
  const context = useContext(ScreenAlertContext);
  if (!context) {
    throw new Error("useScreenAlert must be used within a ScreenAlertProvider");
  }
  return context;
};
